// Gate Intake auto-grouping: the confirmed delivery a new fruit line joins as
// a second load, instead of starting a delivery of its own. Used by the manual
// intake and the WhatsApp-draft confirm on the Deliveries page.

import { supabase } from '@/lib/supabase'
import type { ContainerType } from '@/lib/containerTypes'
import type { IntakePath } from '@/types/database'

// Returns today's date range in SAST (UTC+2)
function todayRangeSAST() {
  const nowSA  = new Date(Date.now() + 2 * 60 * 60 * 1000)
  const dayStr = nowSA.toISOString().slice(0, 10)
  return {
    start: new Date(`${dayStr}T00:00:00+02:00`).toISOString(),
    end:   new Date(`${dayStr}T23:59:59+02:00`).toISOString(),
  }
}

interface TodaysDelivery {
  delivery_id:    string
  bins_received:  number
  bins_remaining: number
  version:        number
  // Intake weight (mig 20261185000000): a weighed load joins only a weighed delivery's sum.
  gross_weight_kg?: number | null
  other_tare_kg?:   number | null
}

// Find an existing confirmed delivery for same puc + variety + block + fruit
// container + intake path today (for grouping). The container is part of the
// group key: two bin types of the same variety on one truck must stay separate
// deliveries, because the per-delivery container drives the tare that
// tipping's net weight deducts — merging them would mis-weigh one load.
//
// The intake path is part of the key too. A load that joins a delivery takes
// that delivery's path for its bins: drench_revenue bills per DELIVERY
// (intake_path = NOT_DRENCHED, i.e. OG, × bins_received) and the delivery's
// status follows its path. Without it, a Straight to Cold Room peach line
// joined the morning's OG peach delivery and was billed a drench it never had;
// an Already Drenched line joining an OG delivery was billed one too, and an
// OG line joining an Already Drenched one lost its drench.
//
// A failed read answers null ("nothing to join"), so the line saves as a
// delivery of its own (unchanged from when this lived in Deliveries.tsx).
export async function findTodaysDelivery(
  pucId: string,
  variety: string,
  blockId: string | null,
  fruit: { fruit_container_type: ContainerType | null; fruit_bin_type_id: string | null },
  intakePath: IntakePath,
): Promise<TodaysDelivery | null> {
  const { start, end } = todayRangeSAST()
  let q = supabase
    .from('deliveries')
    // version included so the auto-group merge path can call
    // updateWithVersion() instead of a bare UPDATE — two operators
    // capturing concurrent second-loads can no longer last-write-wins
    // each other's bin counts.
    .select('delivery_id, bins_received, bins_remaining, version, gross_weight_kg, other_tare_kg')
    .eq('puc_id', pucId)
    .eq('variety', variety)
    .eq('intake_path', intakePath)   // see the header: the drench is billed per delivery
    .not('status', 'in', '("DRAFT","TIPPED")')
    .is('deleted_at', null)
    .gte('received_at', start)
    .lte('received_at', end)
  // The block is part of the key (spec deliveries-corrections §2.6): two
  // blocks of one PUC + variety on one day are two deliveries, not two loads —
  // a load row carries no block of its own and the Phytclean gate is per block.
  q = blockId ? q.eq('block_id', blockId) : q.is('block_id', null)
  q = fruit.fruit_bin_type_id
    ? q.eq('fruit_bin_type_id', fruit.fruit_bin_type_id)
    : fruit.fruit_container_type
      ? q.eq('fruit_container_type', fruit.fruit_container_type).is('fruit_bin_type_id', null)
      : q.is('fruit_container_type', null).is('fruit_bin_type_id', null)
  const { data } = await q
    .order('received_at', { ascending: true })
    .limit(1)
    .maybeSingle()
  return data as TodaysDelivery | null
}
